// relative
import type { NavItemConfig, NavSectionConfig } from '../navigationTypes';

/**
 * Utility to merge dynamic children into a navigation config
 *
 * Finds a nav item by dot-notation path (e.g., 'energyxplorer.deepdive')
 * and replaces or merges its children with the provided dynamic children.
 *
 * @param config - The base navigation config
 * @param targetPath - Dot-notation path to the target item (e.g., 'section.item.subitem')
 * @param dynamicChildren - Array of NavItemConfig to merge in
 * @param mode - 'replace' (default) or 'merge' to append to existing children
 * @returns New config with merged children
 *
 * @example
 * ```tsx
 * const energyGroupItems = useEnergyGroupNavItems();
 * const config = mergeNavChildren(
 *   NAV_CONFIG,
 *   'energyxplorer.deepdive',
 *   energyGroupItems
 * );
 * ```
 */
export const mergeNavChildren = (
  config: NavSectionConfig[],
  targetPath: string,
  dynamicChildren: NavItemConfig[],
  mode: 'replace' | 'merge' = 'replace',
): NavSectionConfig[] => {
  const pathParts = targetPath.split('.');

  const mergeIntoItem = (
    item: NavItemConfig,
    remainingPath: string[],
  ): NavItemConfig => {
    // Base case: we've reached the target
    if (remainingPath.length === 0) {
      return {
        ...item,
        children:
          mode === 'replace'
            ? dynamicChildren
            : [...(item.children ?? []), ...dynamicChildren],
      };
    }

    // Recursive case: continue searching children
    if (!item.children) return item;

    const [nextId, ...rest] = remainingPath;
    return {
      ...item,
      children: item.children.map((child) =>
        child.id === nextId ? mergeIntoItem(child, rest) : child,
      ),
    };
  };

  const mergeIntoSection = (section: NavSectionConfig): NavSectionConfig => {
    const [firstId, ...rest] = pathParts;

    // Check if this section matches or contains the target
    if (section.id !== firstId) return section;

    // If path has more parts, search in items
    if (rest.length > 0) {
      const [itemId, ...itemRest] = rest;
      return {
        ...section,
        items: section.items.map((item) =>
          item.id === itemId ? mergeIntoItem(item, itemRest) : item,
        ),
      };
    }

    return section;
  };

  return config.map(mergeIntoSection);
};

/**
 * Find a navigation item by dot-notation path
 *
 * @param config - The navigation config to search
 * @param targetPath - Dot-notation path (e.g., 'energyxplorer.deepdive')
 * @returns The found item or undefined
 */
export const findNavItem = (
  config: NavSectionConfig[],
  targetPath: string,
): NavItemConfig | undefined => {
  const pathParts = targetPath.split('.');

  const searchInItem = (
    item: NavItemConfig,
    remainingPath: string[],
  ): NavItemConfig | undefined => {
    if (remainingPath.length === 0) return item;
    if (!item.children) return undefined;

    const [nextId, ...rest] = remainingPath;
    const child = item.children.find((c) => c.id === nextId);
    return child ? searchInItem(child, rest) : undefined;
  };

  for (const section of config) {
    const [firstId, ...rest] = pathParts;
    if (section.id !== firstId) continue;

    if (rest.length === 0) {
      // Path points to section itself, not an item
      return undefined;
    }

    const [itemId, ...itemRest] = rest;
    const item = section.items.find((i) => i.id === itemId);
    if (item) {
      return searchInItem(item, itemRest);
    }
  }

  return undefined;
};
